import {
  ArrowUpRight,
  GraduationCap,
  BookOpenCheck,
  Heart,
  Compass,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { BOOK_TRIAL_URL } from '../lib/config';
import { useSite } from '../lib/SiteContentContext';
import { imageAttrs } from '../lib/imageUrl';

const ABOUT_HERO_SIZES = '(min-width: 1024px) 40vw, 100vw';

const VALUES = [
  {
    icon: GraduationCap,
    title: 'Every lesson taught by a qualified UK teacher and examiner.',
  },
  {
    icon: BookOpenCheck,
    title: 'Every course built around AQA and OCR specifications.',
  },
  {
    icon: Heart,
    title: 'Every student supported beyond the live lesson.',
  },
  {
    icon: Compass,
    title: 'Every programme structured around learning, practice and progress.',
  },
];

export default function About() {
  const { siteSettings } = useSite();
  const aboutImgProps = imageAttrs(siteSettings?.aboutImage, {
    sizes: ABOUT_HERO_SIZES, fallbackWidth: 800,
  });
  return (
    <main>
      {/* Hero - Our story */}
      <section className="pt-10 md:pt-16 pb-10 md:pb-14 bg-white">
        <div className="container">
          <div className={aboutImgProps.src ? 'grid lg:grid-cols-2 gap-10 lg:gap-14 items-center' : 'max-w-3xl'}>
            <div className={aboutImgProps.src ? '' : 'max-w-3xl'}>
              <div className="text-brand-coral text-sm font-semibold uppercase tracking-widest">
                Our story
              </div>
              <h1 className="mt-2 font-display text-4xl sm:text-5xl md:text-6xl text-brand-ink leading-[1.05]">
                Built by teachers.{' '}
                <span className="italic text-brand-teal">Designed around students.</span>
              </h1>
              <p className="mt-5 text-brand-ink/80 text-base md:text-lg leading-relaxed">
                Science Sorted was founded to provide the kind of structured, exam-focused science
                tuition we believe students deserve: qualified teachers and examiners, carefully
                organised programmes, evidence-informed learning and regular support beyond the live
                lesson.
              </p>
              <p className="mt-4 text-brand-ink/70 text-base md:text-lg leading-relaxed">
                Founded by <span className="text-brand-ink font-semibold">Qasid Ahmed</span>, a
                qualified UK science teacher with over ten years&rsquo; classroom experience, a PGCE
                from King&rsquo;s College London and current examining experience with AQA and OCR.
              </p>
            </div>
            {aboutImgProps.src && (
              <div className="rounded-3xl overflow-hidden aspect-[4/5] lg:aspect-[4/4] shadow-xl">
                <img
                  {...aboutImgProps}
                  alt="About Science Sorted"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* What we stand for */}
      <section className="py-12 md:py-16 bg-brand-cream" aria-labelledby="values-heading">
        <div className="container">
          <div className="max-w-3xl">
            <div className="text-brand-coral text-sm font-semibold uppercase tracking-widest">
              What we stand for
            </div>
            <h2
              id="values-heading"
              className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl text-brand-ink"
            >
              Four things we <span className="italic text-brand-teal">never compromise on</span>.
            </h2>
          </div>

          <ul className="mt-8 md:mt-10 grid sm:grid-cols-2 gap-4 md:gap-5 max-w-4xl">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <li
                  key={v.title}
                  className="flex items-start gap-4 p-5 md:p-6 rounded-3xl bg-white border border-brand-ink/10"
                >
                  <div className="shrink-0 w-11 h-11 rounded-2xl bg-brand-sun/90 grid place-items-center">
                    <Icon className="w-5 h-5 text-brand-ink" aria-hidden="true" />
                  </div>
                  <p className="text-base md:text-lg text-brand-ink leading-snug">{v.title}</p>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Why Science Sorted? - founder story, with Qasid's photo alongside */}
      <section className="py-12 md:py-16 bg-brand-mint/30" aria-labelledby="why-founder-heading">
        <div className="container">
          <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-10 lg:gap-14 items-start">
            <div>
              <div className="text-brand-coral text-sm font-semibold uppercase tracking-widest">
                Why Science Sorted?
              </div>
              <h2
                id="why-founder-heading"
                className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl text-brand-ink leading-tight"
              >
                Tuition should be more than{' '}
                <span className="italic text-brand-teal">one good hour a week</span>.
              </h2>

              <div className="mt-6 md:mt-8 space-y-4 text-brand-ink/80 text-base md:text-lg leading-relaxed">
                <p>
                  After 10+ years teaching science in UK schools - and through my work as
                  a current AQA and OCR examiner - I&rsquo;ve seen that students rarely
                  struggle because they simply need another explanation. More often, they
                  need structure, regular practice and a clear understanding of what the
                  exam actually requires.
                </p>
                <p>
                  That is why Science Sorted goes beyond the live lesson. Students follow
                  an organised programme that combines clear teaching with retrieval
                  practice, targeted exam questions, regular assessment and feedback.
                  Important knowledge is revisited throughout the course so that students
                  are not constantly learning, forgetting and starting again.
                </p>
                <p>
                  We also place a strong emphasis on organisation and accountability.
                  Students are given clear tasks, regular reminders and progress check-ins
                  so they know what to work on and stay on track. The aim is simple: to
                  provide the structure and support students need to become more
                  confident, independent and effective scientists.
                </p>
              </div>

              <p className="mt-6 md:mt-8 text-sm md:text-base text-brand-ink/70">
                - Qasid Ahmed, Founder
              </p>
            </div>

            <figure className="mx-auto lg:mx-0 lg:mt-40 max-w-sm w-full">
              <div className="relative rounded-3xl overflow-hidden shadow-xl aspect-[4/3] bg-white">
                <img
                  src="/team/qasid-ahmed-headshot.jpg"
                  alt="Qasid Ahmed - founder of Science Sorted and qualified UK science teacher"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <figcaption className="mt-3 text-center lg:text-left">
                <div className="font-semibold text-brand-ink">Qasid Ahmed</div>
                <div className="text-sm text-brand-ink/70">
                  Founder &amp; Lead GCSE Science and Biology Teacher
                </div>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* Slim CTA */}
      <section className="py-10 md:py-14" aria-labelledby="about-cta-heading">
        <div className="container">
          <div className="rounded-3xl bg-brand-sun px-6 md:px-10 py-6 md:py-7 flex flex-col md:flex-row md:items-center gap-4 md:gap-6 md:justify-between">
            <div>
              <h3
                id="about-cta-heading"
                className="font-display text-xl md:text-2xl text-brand-ink leading-tight"
              >
                Meet us in a free trial lesson.
              </h3>
              <p className="mt-1 text-brand-ink/80 text-sm md:text-base">
                One free hour with a qualified UK teacher. No obligation to continue.
              </p>
            </div>
            <Button
              asChild
              size="lg"
              className="bg-brand-ink text-brand-cream hover:bg-brand-teal rounded-full h-12 px-6 shrink-0"
            >
              <a
                href={BOOK_TRIAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="gap-2"
                data-testid="about-book-trial-cta"
              >
                Book a free trial <ArrowUpRight className="w-4 h-4" />
              </a>
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
}
